// AGC: Lot Progress Calculation Utility
// AGC: Calculates real-time progress based on planting date and crop cycle
// AGC: Replaces hardcoded/mock progress values with actual time-based calculation

import { Plot } from '../types'
import { CROP_CYCLE_DATA, getCurrentGrowthStage, type LotGrowthStage } from '../types/lot'

/**
 * AGC: Crop cycle data for different crops (fallback if not in CROP_CYCLE_DATA)
 */
const DEFAULT_CROP_CYCLES: Record<string, number> = {
  soja: 120,
  milho: 140,
  trigo: 130,
  cafe: 180,
  algodao: 180,
  feijao: 90,
  arroz: 120,
  outros: 120,
  // AGC: Add more as needed
}

/**
 * AGC: Get the total cycle days for a crop type
 * @param cropType - The crop type (soja, milho, trigo, etc.)
 * @returns The total cycle days for the crop
 */
export const getCropCycleDays = (cropType: string): number => {
  // AGC: Try to get from CROP_CYCLE_DATA first
  const cropData = CROP_CYCLE_DATA[cropType]
  if (cropData) {
    return cropData.cycleDays
  }
  
  // AGC: Fallback to DEFAULT_CROP_CYCLES
  return DEFAULT_CROP_CYCLES[cropType] || DEFAULT_CROP_CYCLES.outros
}

/**
 * AGC: Calculate days since planting
 * @param plantingDate - The planting date (ISO string or Date)
 * @param currentDate - Optional current date (defaults to now)
 * @returns Number of days since planting (0 or positive)
 */
export const getDaysSincePlanting = (
  plantingDate: string | Date | undefined,
  currentDate: Date = new Date()
): number => {
  if (!plantingDate) {
    return 0
  }
  
  const planting = typeof plantingDate === 'string' ? new Date(plantingDate) : plantingDate
  const diffMs = currentDate.getTime() - planting.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  
  // AGC: Return 0 if planting date is in the future
  return Math.max(0, diffDays)
}

/**
 * AGC: Calculate lot progress based on planting date and crop cycle
 * 
 * @param plot - The lot/plot object
 * @param currentDate - Optional current date (defaults to now, useful for testing)
 * @returns Progress percentage (0-100)
 * 
 * Formula:
 *   elapsedDays = today - plantingDate (capped at 0 and cycleDays)
 *   progressPercent = (elapsedDays / cycleDays) * 100 (clamped to 0-100)
 * 
 * Examples:
 *   - New lot with plantingDate = today → progress ≈ 0%
 *   - Lot planted 60 days ago with 120-day cycle → progress = 50%
 *   - Lot planted 200 days ago with 120-day cycle → progress = 100%
 */
export const calculateLotProgress = (
  plot: Pick<Plot, 'plantingDate' | 'cropType'> & { createdAt?: string },
  currentDate: Date = new Date()
): number => {
  const { plantingDate, cropType, createdAt } = plot

  // AGC: Use plantingDate, fallback to createdAt (matches detail page logic in useLotWeatherHealth)
  const effectiveDate = plantingDate || createdAt
  if (!effectiveDate) {
    return 0
  }

  const cycleDays = getCropCycleDays(cropType)
  const daysSincePlanting = getDaysSincePlanting(effectiveDate, currentDate)
  
  // AGC: Cap elapsed days at the cycle length
  const elapsedDays = Math.min(daysSincePlanting, cycleDays)
  
  // AGC: Calculate progress percentage (0-100)
  const progress = Math.round((elapsedDays / cycleDays) * 100)
  
  // AGC: Clamp to 0-100 range (shouldn't be needed but safety first)
  return Math.max(0, Math.min(100, progress))
}

/**
 * AGC: Calculate lot progress with full growth stage information
 * @param plot - The lot/plot object
 * @param currentDate - Optional current date
 * @returns Object with progress, growth stage, and timing info
 */
export const calculateLotProgressDetailed = (
  plot: Pick<Plot, 'plantingDate' | 'cropType'> & { createdAt?: string },
  currentDate: Date = new Date()
): {
  progress: number
  daysSincePlanting: number
  cycleTotalDays: number
  daysToHarvest: number
  growthStage: LotGrowthStage
  estimatedHarvestDate: Date | null
} => {
  const { plantingDate, cropType, createdAt } = plot
  const cycleTotalDays = getCropCycleDays(cropType)

  // AGC: Use plantingDate, fallback to createdAt (matches detail page logic)
  const effectiveDate = plantingDate || createdAt
  if (!effectiveDate) {
    return {
      progress: 0,
      daysSincePlanting: 0,
      cycleTotalDays,
      daysToHarvest: cycleTotalDays,
      growthStage: getCurrentGrowthStage(cropType, 0),
      estimatedHarvestDate: null,
    }
  }

  const daysSincePlanting = getDaysSincePlanting(effectiveDate, currentDate)
  const growthStage = getCurrentGrowthStage(cropType, daysSincePlanting)
  const progress = calculateLotProgress(plot, currentDate)

  // AGC: Calculate estimated harvest date
  const plantingDateObj = new Date(effectiveDate)
  const estimatedHarvestDate = new Date(
    plantingDateObj.getTime() + cycleTotalDays * 24 * 60 * 60 * 1000
  )
  
  return {
    progress,
    daysSincePlanting,
    cycleTotalDays,
    daysToHarvest: growthStage.daysToHarvest,
    growthStage,
    estimatedHarvestDate,
  }
}

/**
 * AGC: Get a display-friendly description of the progress
 * @param progress - Progress percentage (0-100)
 * @returns A description string
 */
export const getProgressDescription = (progress: number): string => {
  if (progress === 0) return 'Não iniciado'
  if (progress < 25) return 'Início'
  if (progress < 50) return 'Desenvolvimento'
  if (progress < 75) return 'Avançado'
  if (progress < 100) return 'Final'
  return 'Completo'
}

/**
 * AGC: Get the progress bar color based on progress percentage
 * @param progress - Progress percentage (0-100)
 * @returns Tailwind color class
 */
export const getProgressColor = (progress: number): string => {
  if (progress < 25) return 'bg-gray-400'
  if (progress < 50) return 'bg-yellow-400'
  if (progress < 75) return 'bg-emerald-400'
  if (progress < 100) return 'bg-emerald-500'
  return 'bg-emerald-600'
}

export default {
  calculateLotProgress,
  calculateLotProgressDetailed,
  getCropCycleDays,
  getDaysSincePlanting,
  getProgressDescription,
  getProgressColor,
}


